import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Fullscreen as FullscreenIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon
} from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { getImageDimensions } from '../../../../utils/mediaDetector';

const MENSURE_FALLBACK_IMAGE = '/mensure-thumbnail.png';

const handleImageFallback = (event) => {
  if (event.currentTarget.src.includes(MENSURE_FALLBACK_IMAGE)) return;
  event.currentTarget.src = MENSURE_FALLBACK_IMAGE;
  event.currentTarget.style.objectFit = 'contain';
};

const getPrintPages = (article) => {
  if (!article) return [];

  const byImage = new Map();
  const addPage = (page, fallbackNumber) => {
    const image = typeof page === 'string' ? page : page?.image || page?.url;
    if (!image || byImage.has(image)) return;
    byImage.set(image, {
      ...(typeof page === 'string' ? {} : page),
      pageNumber: (typeof page === 'string' ? fallbackNumber : page?.pageNumber) || byImage.size + 1,
      image,
      thumbnail: typeof page === 'string' ? null : page?.thumbnail
    });
  };

  if (Array.isArray(article.pages)) {
    article.pages.forEach((page, index) => addPage(page, index + 1));
  }

  addPage({
    pageNumber: 1,
    image: article.image,
    thumbnail: article.thumbnail,
    cropArea: article.cropArea,
    markers: article.markers
  }, 1);

  if (Array.isArray(article.allImages)) {
    article.allImages.forEach((image, index) => addPage(image, index + 1));
  }

  if (Array.isArray(article.carouselMedia)) {
    article.carouselMedia
      .filter(media => media?.type === 'image')
      .forEach((media, index) => addPage(media, index + 1));
  }

  return Array.from(byImage.values()).map((page, index) => ({
    ...page,
    pageNumber: page.pageNumber || index + 1
  }));
};

const getCroppedUrl = (imageUrl, crop) => {
  if (!imageUrl || !imageUrl.includes('cloudinary.com') || !crop) {
    return imageUrl;
  }

  const { x, y, width, height } = crop;

  if (x === undefined || y === undefined || width === undefined || height === undefined) {
    return imageUrl;
  }

  const relX = Math.min(1, (x / 100) + 0.002).toFixed(5);
  const relY = (y / 100).toFixed(5);
  const relW = Math.min(1, (width / 100) + 0.005).toFixed(5);
  const relH = Math.min(1, (height / 100) + 0.007).toFixed(5);
  const transformation = `fl_relative,c_crop,w_${relW},h_${relH},x_${relX},y_${relY}`;

  return imageUrl.replace(
    '/image/upload/',
    `/image/upload/${transformation}/`
  );
};

const getCropImageStyle = (crop) => {
  if (!crop) return {};

  const { x = 0, y = 0, width = 100, height = 100 } = crop;
  const safeWidth = Math.max(width, 1);
  const safeHeight = Math.max(height, 1);

  return {
    width: `${10000 / safeWidth}%`,
    height: `${10000 / safeHeight}%`,
    maxWidth: 'none',
    transform: `translate(-${x}%, -${y}%)`,
    transformOrigin: 'top left'
  };
};

const getValidPrintAreas = (source) => {
  const ocrMarkers = source?.markers?.filter((marker) => marker.type === 'ocr');
  const hasMarkers = ocrMarkers && ocrMarkers.length > 0;
  const hasLegacyCrop = !!source?.cropArea;

  if (!hasMarkers && !hasLegacyCrop) return [];

  const areasToRender = hasMarkers
    ? ocrMarkers.map((marker) => marker.coordinates)
    : (Array.isArray(source.cropArea) ? source.cropArea : [source.cropArea]);

  return areasToRender.filter((crop) => {
    if (!crop) return false;
    const { x, y, width, height } = crop;
    return x !== undefined && y !== undefined &&
      width !== undefined && height !== undefined;
  });
};

const ArticleModalPrimaryImage = ({ article, youtubeId, onOpenFullscreen }) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const printPages = useMemo(() => getPrintPages(article), [article]);
  const hasMultiplePages = printPages.length > 1;
  const currentPage = printPages[currentPageIndex] || printPages[0];
  const currentImage = currentPage?.image || article.image;

  useEffect(() => {
    setCurrentPageIndex(0);
  }, [article._id]);

  const shouldShowImage = article.mediaType !== 'LinkedIn' &&
    article.mediaType !== 'Radio' &&
    article.mediaType !== 'Rádio' &&
    !(article.contentType === 'story' && article.videoUrl) &&
    !(article.videoStorageMethod === 'cloudinary') &&
    article.image && !youtubeId &&
    (
      !article.videoUrl ||
      (article.mediaType === 'Instagram' && !article.embedCode) ||
      article.mediaType === 'Twitter'
    ) &&
    !(article.mediaType === 'Instagram' && article.embedCode) &&
    !(article.mediaType === 'Twitter') &&
    !(article.mediaType === 'TikTok' && article.embedCode) &&
    !(article.mediaType === 'Threads' && article.embedCode) &&
    !(article.mediaType === 'Facebook' && article.videoStorageMethod === 'cloudinary') &&
    !(article.mediaType === 'Facebook' && article.embedCode) &&
    !(article.embedCode && (
      article.embedCode.includes('videos.globo.com') ||
      article.embedCode.includes('record.r7.com') ||
      article.embedCode.includes('spotify.com')
    ));

  if (!shouldShowImage) return null;

  const imageDimensions = getImageDimensions(article);
  const isPrintMedia = ['Jornal', 'Revista'].includes(article.mediaType);
  const printFullscreenButton = isPrintMedia ? (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
      <Tooltip title="Ver página original com zoom">
        <Button
          size="small"
          variant="outlined"
          startIcon={<FullscreenIcon />}
          onClick={() => onOpenFullscreen(currentPageIndex)}
        >
          Página Original
        </Button>
      </Tooltip>
    </Box>
  ) : null;

  const carouselControls = isPrintMedia && hasMultiplePages ? (
    <>
      <Box sx={{
        position: 'absolute',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 0.5,
        zIndex: 3
      }}>
        {printPages.map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrentPageIndex(index)}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: index === currentPageIndex
                ? 'rgba(255, 255, 255, 0.95)'
                : 'rgba(255, 255, 255, 0.45)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
          />
        ))}
      </Box>
      {currentPageIndex > 0 && (
        <IconButton
          onClick={() => setCurrentPageIndex(prev => Math.max(0, prev - 1))}
          sx={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 36,
            height: 36,
            zIndex: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.82)',
            color: '#262626',
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' }
          }}
        >
          <PrevIcon />
        </IconButton>
      )}
      {currentPageIndex < printPages.length - 1 && (
        <IconButton
          onClick={() => setCurrentPageIndex(prev => Math.min(printPages.length - 1, prev + 1))}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 36,
            height: 36,
            zIndex: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.82)',
            color: '#262626',
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' }
          }}
        >
          <NextIcon />
        </IconButton>
      )}
    </>
  ) : null;

  if (isPrintMedia) {
    const validAreas = getValidPrintAreas(currentPage || article);

    if (validAreas.length > 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2, width: '100%' }}>
          {printFullscreenButton}
          {validAreas.map((crop, index) => {
            const croppedUrl = getCroppedUrl(currentImage, crop);
            const usesCloudinaryCrop = croppedUrl !== currentImage;

            return (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  position: 'relative',
                  display: 'inline-block',
                  maxWidth: '100%',
                  ...(!usesCloudinaryCrop && {
                    width: '100%',
                    aspectRatio: `${Math.max(crop.width || 100, 1)} / ${Math.max(crop.height || 100, 1)}`,
                    maxHeight: 520,
                    overflow: 'hidden',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    bgcolor: 'grey.100'
                  })
                }}
              >
                {validAreas.length > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Parte {index + 1} de {validAreas.length}
                  </Typography>
                )}
                <img
                  src={croppedUrl}
                    onError={handleImageFallback}
                  alt={`${article.title || 'Publicação'} - Página ${currentPageIndex + 1} - Parte ${index + 1}`}
                    style={{
                    ...(usesCloudinaryCrop ? {} : {
                      ...getCropImageStyle(crop),
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }),
                    maxWidth: usesCloudinaryCrop ? '100%' : 'none',
                    height: usesCloudinaryCrop ? 'auto' : getCropImageStyle(crop).height,
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                />
                {index === 0 && carouselControls}
              </Box>
            );
          })}
        </Box>
      );
    }
  }

  return (
    <Box sx={{ mb: 2, width: '100%' }}>
      {printFullscreenButton}
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
          <img
            src={currentImage}
            onError={handleImageFallback}
            alt={article.title}
            style={{
            maxWidth: imageDimensions.maxWidth,
            width: imageDimensions.width,
            height: imageDimensions.height,
            display: 'block',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            objectFit: imageDimensions.objectFit,
            boxSizing: 'border-box',
            margin: imageDimensions.margin
          }}
          />
          {carouselControls}
        </Box>
      </Box>
    </Box>
  );
};

ArticleModalPrimaryImage.propTypes = {
  article: PropTypes.shape({
    _id: PropTypes.string,
    contentType: PropTypes.string,
    cropArea: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    embedCode: PropTypes.string,
    image: PropTypes.string,
    markers: PropTypes.arrayOf(PropTypes.shape({
      coordinates: PropTypes.object,
      type: PropTypes.string
    })),
    mediaType: PropTypes.string,
    pages: PropTypes.arrayOf(PropTypes.shape({
      cropArea: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
      image: PropTypes.string,
      markers: PropTypes.array,
      pageNumber: PropTypes.number,
      thumbnail: PropTypes.string
    })),
    title: PropTypes.string,
    videoStorageMethod: PropTypes.string,
    videoUrl: PropTypes.string
  }).isRequired,
  onOpenFullscreen: PropTypes.func.isRequired,
  youtubeId: PropTypes.string
};

ArticleModalPrimaryImage.defaultProps = {
  youtubeId: null
};

export default ArticleModalPrimaryImage;




